import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  BokkzAlgDataService,
  BokkzWageDataService, ClientEntity, Component1, Component2, Dienstverband, LoonstaatEntity,
  Periode, VerloningsPeriode, WerknemerEntity,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SharedUiDetailsModule} from "@snelstart/ui-details";
import {SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {SharedUiButtonModule} from "@snelstart/ui-button";
import {SharedUiTabModule} from "@snelstart/ui-tab";
import {SharedUiDialogModule} from "@snelstart/ui-dialog";
import {SharedUiAlertModule} from "@snelstart/ui-alert";
import {filter, Observable, shareReplay, tap} from 'rxjs';
import {VariabelevergoedingComponent} from "../variabelevergoeding/variabelevergoeding.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

type ComponentFormGroup = FormGroup<{
  periode: PeriodeFormGroup;
  component: FormControl<Component2 | null>;
  displayValue: FormControl<string | null>;
  eenheid: FormControl<number | null>;
  bedrag: FormControl<number | null>;
  percentage: FormControl<number | null>;
  proRataVergoeding: FormControl<number | null>;
  grondslagComponenten: FormControl<number[] | null>;
  periodeVrijval: FormControl<number | null>;
  cumulatiefSaldoBeginPeriode: FormControl<number | null>;
  berekendeAanwas: FormControl<number | null>;
  cumulatiefSaldoEindPeriode: FormControl<number | null>;
}>;

type LijstFormGroup = FormGroup<{
  berekendeVergoedingen: FormArray<ComponentFormGroup>;
  vasteVergoedingen: FormArray<ComponentFormGroup>;
  variabeleVergoedingen: FormArray<ComponentFormGroup>;
  regelingen: FormArray<ComponentFormGroup>;
  totaalVast: FormControl<number | null>;
  totaalVariabel: FormControl<number | null>;
  totaalInhouding: FormControl<number | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-salarisstrook',
    standalone: true,
    imports: [
        CommonModule,
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        SharedUiButtonModule,
        SharedUiTabModule,
        SharedUiDialogModule,
        SharedUiAlertModule,
        VariabelevergoedingComponent,
    ],
    templateUrl: './salarisstrook.component.html',
    styleUrl: './salarisstrook.component.scss',
})
export class SalarisstrookComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    public employeeId: any;
    private readonly destroyRef = inject(DestroyRef);
    private readonly algDataService = inject(BokkzAlgDataService);
    private readonly dataService = inject(BokkzWageDataService);

    private werknemerEntity = {} as WerknemerEntity;
    private loonstaatEntity = {} as LoonstaatEntity;

    public selectedTab = 0;
    public selectedTabDv = 0;
    public selectedPeriode = 0;
    public index = 0;
    public periode?: Periode;
    public isVerzonden = false;
    public startDatePeriode?: Date;
    public endDatePeriode?: Date;

    public lijstFg: LijstFormGroup = new FormGroup({
        berekendeVergoedingen: new FormArray<ComponentFormGroup>([]),
        vasteVergoedingen: new FormArray<ComponentFormGroup>([]),
        variabeleVergoedingen: new FormArray<ComponentFormGroup>([]),
        regelingen: new FormArray<ComponentFormGroup>([]),
        totaalVast: new FormControl<number | null>(null),
        totaalVariabel: new FormControl<number | null>(null),
        totaalInhouding: new FormControl<number | null>(null),
    });

    readonly clientEntity$: Observable<ClientEntity> = this.algDataService.clientEntity$.pipe(
        filter((data): data is ClientEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly werknemerEntity$: Observable<WerknemerEntity> = this.dataService.werknemerEntity$.pipe(
        filter((data): data is WerknemerEntity => !!data?.id),
        tap((data) => {
            this.werknemerEntity = data;
            this.employeeId = data.id;
            this.syncForms();
        }),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly loonstaatEntity$: Observable<LoonstaatEntity> = this.dataService.loonstaatEntity$.pipe(
        filter((data): data is LoonstaatEntity => !!data?.loonStaat),
        tap((data) => {
            this.loonstaatEntity = data;
            this.employeeId = data.id;
            this.syncForms();
        }),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly selectedPeriode$: Observable<number> = this.dataService.selectedPeriode$.pipe(
        filter((data): data is number => !!data),
        tap((data) => {
            this.selectedPeriode = data;
            this.syncForms();
        }),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    ngOnInit(): void {
        this.clientEntity$.subscribe();
        this.werknemerEntity$.subscribe();
        this.loonstaatEntity$.subscribe();
        this.selectedPeriode$.subscribe();
    }

    public get hasWerknemerGegevens(): boolean {
        return this.werknemerEntity.werknemerGegevens !== undefined;
    }

    public openVariabeleVergoedingTab(): void {
        this.dataService.updateSelectedTab(2);
    }

    private getCurrentVerloningsPeriode(): VerloningsPeriode | undefined {
        const periodes = this.loonstaatEntity.loonStaat?.verloningsPeriode ?? [];
        return periodes.find((periode) => periode.periodenummer === this.selectedPeriode) ?? periodes.at(this.selectedPeriode - 1);
    }

    private syncForms(): void {
        if (!this.werknemerEntity?.werknemerGegevens) {
            return;
        }

        const verloningsPeriode = this.getCurrentVerloningsPeriode();
        this.applyPeriodeContext(verloningsPeriode);

        this.updateFormLoonstaat(verloningsPeriode);
        this.updateFormVasteVergoedingen();
        this.updateFormVariabeleVergoedingen();
        this.updateFormReserveringen();
    }

    private applyPeriodeContext(verloningsPeriode?: VerloningsPeriode): void {
        this.periode = verloningsPeriode?.periode;
        const periodeStatus = verloningsPeriode as { isVerzonden?: boolean; afgesloten?: boolean } | undefined;
        this.isVerzonden = Boolean(periodeStatus?.isVerzonden || periodeStatus?.afgesloten);
        this.startDatePeriode = this.periode?.begindatum;
        this.endDatePeriode = this.periode?.einddatum;
    }

    ngOnDestroy(): void {
        // this.bokkzClient
        //   .postOneEmployee(String(this.clientId), this.werknemerEntity)
        //   .pipe(takeUntil(this.destroy$))
        //   .subscribe({
        //     error: (error) => {
        //       console.log(error);
        //     }
        //   });
        // this.destroy$.next();
        // this.destroy$.complete();
    }

    get vergoedingen(): FormArray<ComponentFormGroup> {
        return this.lijstFg.controls.berekendeVergoedingen;
    }

    get getVasteVergoedingen(): FormArray<ComponentFormGroup> {
        return this.lijstFg.controls.vasteVergoedingen;
    }

    get getVariabeleVergoedingen(): FormArray<ComponentFormGroup> {
        return this.lijstFg.controls.variabeleVergoedingen;
    }

    createFormGroup(isVar: boolean, item?: Component1): ComponentFormGroup {
        return new FormGroup({
            periode: new FormGroup({
                begindatum: new FormControl<Date | null>(isVar ? (this.startDatePeriode ?? null) : (item?.periode?.begindatum ?? null)),
                einddatum: new FormControl<Date | null>(isVar ? (this.endDatePeriode ?? null) : (item?.periode?.einddatum ?? null)),
            }),
            component: new FormControl<Component2 | null>(item?.component ?? null),
            displayValue: new FormControl<string | null>(item?.displayValue ?? null),
            eenheid: new FormControl<number | null>(item?.eenheid ?? null),
            bedrag: new FormControl<number | null>(item?.bedrag ?? null),
            percentage: new FormControl<number | null>(item?.percentage ?? null),
            proRataVergoeding: new FormControl<number | null>(item?.proRataVergoeding ?? null),
            grondslagComponenten: new FormControl<number[] | null>(item?.grondslagComponenten ?? null),
            periodeVrijval: new FormControl<number | null>(item?.periodeVrijval ?? null),
            cumulatiefSaldoBeginPeriode: new FormControl<number | null>(item?.cumulatiefSaldoBeginPeriode ?? null),
            berekendeAanwas: new FormControl<number | null>(item?.berekendeAanwas ?? null),
            cumulatiefSaldoEindPeriode: new FormControl<number | null>(item?.cumulatiefSaldoEindPeriode ?? null),
        });
    }

    private updateFormVasteVergoedingen(): void {
        const dienstverbandLijst = this.werknemerEntity.werknemerGegevens?.dienstverbanden?.at(0)?.vasteVergoedingen || [];

        const verloningsPeriode = this.getCurrentVerloningsPeriode();
        const loonComponenten = verloningsPeriode?.loonComponenten || [];

        // 👉 pak RESERVERING_BEDRAG eruit
        const vakantiegeldComponent = loonComponenten.find(
            (c) => c.component === 'RESERVERING_BEDRAG' && (c.periodeVrijval === this.selectedPeriode || c.periodeVrijval === 99),
        );

        const lijst = [...dienstverbandLijst];

        if (vakantiegeldComponent) {
            lijst.push({
                ...vakantiegeldComponent,
                bedrag: vakantiegeldComponent.bedrag,
            });
        }

        const formArray = this.getVasteVergoedingen;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i);
                control.patchValue(lijst[i]);
            } else {
                const formGroup = this.createFormGroup(false, lijst[i]);
                formArray.push(formGroup);
            }
        }
    }

    private updateFormVariabeleVergoedingen(): void {
        const lijst = this.werknemerEntity.werknemerGegevens?.dienstverbanden?.at(0)?.variabeleVergoedingen || [];
        const formArray = this.getVariabeleVergoedingen;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i);
                control.patchValue(lijst[i]);
            } else {
                const formGroup = this.createFormGroup(true, lijst[i]);
                formArray.push(formGroup);
            }
        }
    }

    private updateFormReserveringen(): void {
        const lijst = this.werknemerEntity.werknemerGegevens?.dienstverbanden?.at(0)?.regelingen || [];
        const formArray = this.lijstFg.controls.regelingen;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i);
                control.patchValue(lijst[i]);
            } else {
                const formGroup = this.createFormGroup(true, lijst[i]);
                formArray.push(formGroup);
            }
        }
    }

    private updateFormLoonstaat(verloningsPeriode?: VerloningsPeriode): void {
        const lijst = verloningsPeriode?.loonComponenten || [];
        const formArray = this.vergoedingen;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i);
                control.patchValue(lijst[i]);
            } else {
                const formGroup = this.createFormGroup(false, lijst[i]);
                formArray.push(formGroup);
            }
        }
    }

    saveForm(): void {
        this.lijstFg.updateValueAndValidity();

        const dienstverband = {
            ...this.werknemerEntity.werknemerGegevens?.dienstverbanden?.at(0),
            vasteVergoedingen: this.lijstFg.getRawValue().vasteVergoedingen,
            variabeleVergoedingen: this.lijstFg.getRawValue().variabeleVergoedingen,
            regelingen: this.lijstFg.getRawValue().regelingen,
        } as Dienstverband;
        this.werknemerEntity = {
            id: this.werknemerEntity.id,
            taxId: this.werknemerEntity.taxId,
            werknemerGegevens: {
                ...this.werknemerEntity.werknemerGegevens,
                ...this.werknemerEntity.werknemerGegevens?.bankrekeningen,
                dienstverbanden: [dienstverband],
            },
        } as WerknemerEntity;
    }

    public getTotaalVast(): any {
        const test = this.getVasteVergoedingen;
        let sum = 0;
        for (let i = 0; i < test.length; i++) {
            const component = test.at(i);
            if (this.showInPeriode(component.value)) {
                sum = sum + (component.controls.proRataVergoeding.value || 0);
            }
        }
        return this.lijstFg.patchValue({ totaalVast: sum });
    }

    public getTotaalVariabel(): any {
        const test = this.getVariabeleVergoedingen;
        let sum = 0;
        for (let i = 0; i < test.length; i++) {
            const component = test.at(i);
            if (this.showInPeriode(component.value)) {
                sum = sum + (component.controls.bedrag.value || 0);
            }
        }
        return this.lijstFg.patchValue({ totaalVariabel: sum });
    }

    public getTotaalInhoudingen(): any {
        const test = this.vergoedingen;
        let sum = 0;
        for (let i = 0; i < test.length; i++) {
            const component = test.at(i);
            if (this.inhoudingComponent(component.controls.component.value)) {
                sum = sum + (component.controls.bedrag.value || 0);
            }
        }
        return this.lijstFg.patchValue({ totaalInhouding: sum });
    }

    public formatComponentLabel(value: Component2 | string | null | undefined, displayValue?: string | null): string {
        if (displayValue === 'Reservering vakantiebijslag') {
            return 'Vakantiegeld';
        }

        if (displayValue) {
            return displayValue;
        }

        if (value === null || value === undefined) return '';

        const label = String(value);
        const formatted = label.toLowerCase().replace(/_/g, ' ');
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }

    public showInPeriode(item: { periode?: { begindatum?: Date | null; einddatum?: Date | null } }) {
        const dateStart = item.periode?.begindatum;
        const dateEnd = item.periode?.einddatum;

        if (!dateStart || !this.startDatePeriode || !this.endDatePeriode) {
            return true;
        }

        let returnItem = true;
        if (new Date(dateStart) > new Date(this.endDatePeriode)) {
            returnItem = false;
        } else if (dateEnd !== null && dateEnd !== undefined && new Date(dateEnd) < new Date(this.startDatePeriode)) {
            returnItem = false;
        }
        return returnItem;
    }

    public inhoudingComponent(item: Component2 | string | null | undefined): boolean {
        if (item === null || item === undefined) {
            return false;
        }

        return this.publicInhoudingComponent.includes(String(item));
    }

    public prorata(index: number): any {
        const item = this.getVasteVergoedingen.at(index).getRawValue();

        if (item === undefined || item === null) {
            return null;
        }

        if (!this.startDatePeriode || !this.endDatePeriode) {
            return this.getVasteVergoedingen.at(index).patchValue({ proRataVergoeding: item.bedrag });
        }

        const periodeBegin = item.periode?.begindatum;
        if (!periodeBegin) {
            return this.getVasteVergoedingen.at(index).patchValue({ proRataVergoeding: item.bedrag });
        }

        if (new Date(periodeBegin).getTime() === new Date(this.startDatePeriode).getTime() && item.periode?.einddatum === null) {
            return this.getVasteVergoedingen.at(index).patchValue({ proRataVergoeding: item.bedrag });
        } else {
            let startDatum: Date = periodeBegin;
            let eindDatum: Date = item.periode?.einddatum ?? this.endDatePeriode;
            let workingDays = 22;
            let totalDays = 22;

            if (new Date(startDatum) < new Date(this.startDatePeriode)) {
                startDatum = this.startDatePeriode;
            }
            if (new Date(eindDatum) > new Date(this.endDatePeriode)) {
                eindDatum = this.endDatePeriode;
            }

            if (item.bedrag) {
                workingDays = this.getWorkingDaysBetween(startDatum, eindDatum);
                totalDays = this.getWeekdaysInMonth(startDatum);

                if (workingDays !== 0) {
                    const bedrag = (item.bedrag / totalDays) * (workingDays + 1);
                    const rounded = this.roundHalfEven(bedrag);

                    if (Math.abs(bedrag) > Math.abs(item.bedrag)) {
                        return this.getVasteVergoedingen.at(index).patchValue({ proRataVergoeding: item.bedrag });
                    } else {
                        return this.getVasteVergoedingen.at(index).patchValue({ proRataVergoeding: rounded });
                    }
                } else return this.getVasteVergoedingen.at(index).patchValue({ proRataVergoeding: item.bedrag });
            } else return this.getVasteVergoedingen.at(index).patchValue({ proRataVergoeding: item.bedrag });
        }
    }

    private getWorkingDaysBetween(start: Date, end: Date): number {
        let count = 0;
        let current = new Date(start);

        while (new Date(current).getTime() < new Date(end).getTime()) {
            const day = current.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
            if (day !== 0 && day !== 6) {
                // Exclude Sunday (0) and Saturday (6)
                count++;
            }
            current.setDate(current.getDate() + 1);
        }
        return count;
    }

    private getWeekdaysInMonth(date: Date): number {
        const year = new Date(date).getFullYear();
        const month = new Date(date).getMonth(); // 0-based

        const totalDays = new Date(year, month + 1, 0).getDate();
        let weekdayCount = 0;

        for (let day = 1; day <= totalDays; day++) {
            const currentDate = new Date(year, month, day);
            const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday

            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                weekdayCount++;
            }
        }
        return weekdayCount;
    }

    private roundHalfEven(value: number, decimals: number = 2): number {
        const factor = Math.pow(10, decimals);
        const scaledValue = value * factor;
        const floor = Math.floor(scaledValue);
        const diff = scaledValue - floor;

        let rounded: number;
        if (diff < 0.5) {
            rounded = floor;
        } else if (diff > 0.5) {
            rounded = floor + 1;
        } else {
            // Exactly halfway: round to the nearest even
            rounded = floor % 2 === 0 ? floor : floor + 1;
        }

        return rounded / factor;
    }

    publicInhoudingComponent = ['LOONHEFFING', 'LOONHEFFING_BIJZONDER_TARIEF', 'BIJDRAGE_ZVW'];
}
